import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ButtonSpinner } from '../components/ui/Loader';
import {
  useVerifyTelegramLoginCodeMutation,
} from '../store/api/authApi';
import { storeAuthData } from '../utils/tokenManager';

export function TelegramLoginVerifyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifyCode, { isLoading }] = useVerifyTelegramLoginCodeMutation();

  const telegramId = searchParams.get('telegramId');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!telegramId) {
      navigate('/telegram-login-request');
    }
  }, [telegramId, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError(t('auth.telegramLogin.codeRequired'));
      return;
    }

    if (!telegramId) {
      navigate('/telegram-login-request');
      return;
    }

    try {
      const result = await verifyCode({
        telegramId: Number.parseInt(telegramId, 10),
        code: code.trim().toUpperCase(),
      }).unwrap();

      if (result.status === 'success' && result.data.user) {
        // Store user data (token is in HttpOnly cookie)
        storeAuthData('', result.data.user);

        // إذا كان للمستخدم بريد إلكتروني مسبقاً لا نعرض صفحة إضافة البريد وكلمة المرور
        const hasEmail = result.data.user?.email?.trim();
        if (hasEmail) {
          navigate('/dashboard');
        } else {
          navigate(`/telegram-login-add-credentials?telegramId=${telegramId}`);
        }
      }
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setError(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setCode(value);
    if (error) setError(null);
  };

  if (!telegramId) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">

      <div className="w-full max-w-md animate-scale-in relative z-10">
        <div className="card-glass rounded-3xl shadow-2xl p-8 space-y-6">
          <div className="text-center space-y-2 animate-slide-down">
            <h1 className="text-4xl font-heading font-bold text-brand-accent">
              {t('auth.telegramLogin.verifyTitle')}
            </h1>
            <p className="text-neutral-medium font-body">
              {t('auth.telegramLogin.verifySubtitle')}
            </p>
          </div>

          {error && (
            <div className="p-4 bg-danger/20 border border-danger rounded-lg animate-slide-down">
              <p className="text-red-300 text-sm font-body">{error}</p>
            </div>
          )}

          <form onSubmit={(e) => { void handleSubmit(e); }} noValidate className="space-y-5">
            <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <label
                htmlFor="code"
                className="block text-sm font-body font-medium text-neutral-light"
              >
                {t('auth.telegramLogin.code')}
              </label>
              <input
                type="text"
                id="code"
                name="code"
                value={code}
                onChange={handleChange}
                maxLength={8}
                className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 transform focus:scale-[1.02] font-body text-center text-2xl tracking-widest font-mono ${
                  error
                    ? 'border-danger focus:ring-danger focus:border-danger'
                    : 'border-neutral-dark focus:border-brand-accent'
                }`}
                placeholder="A1B2C3"
                autoComplete="off"
                autoFocus
              />
              <p className="text-xs text-neutral-medium font-body text-center">
                {t('auth.telegramLogin.codeHint')}
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading || !code.trim()}
              className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none animate-slide-up flex items-center justify-center gap-2"
              style={{ animationDelay: '0.2s' }}
            >
              {isLoading ? (
                <>
                  <ButtonSpinner />
                  <span>{t('common.loading')}</span>
                </>
              ) : (
                t('auth.telegramLogin.verify')
              )}
            </button>
          </form>

          <div className="text-center animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <button
              type="button"
              onClick={() => navigate('/telegram-login-request')}
              className="text-sm text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-body"
            >
              {t('common.back')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

